import type { PulseChart } from '../chart.js';
import type { ReplayInputs } from '../replay.js';
import type { Variant } from '../gameTypes.js';
import { VARIANTS, buildVariantSlug } from '../gameTypes.js';
import { buildVariantTitle } from '../chart.js';
import { ChartBuilder, expectedReplayFromPads } from '../engine/pulse/chartBuilder.js';
import { PALETTE_GLACIER } from './palettes.js';
import {
  buildBuiltinAudio,
  findBurstChambers,
  mergeBeats,
  variantMergeBeatsWithSpacing,
  variantSelectBeats,
} from './audioFromOnsets.js';
import onsets from './audio/blank.onsets.json' with { type: 'json' };

const audio = buildBuiltinAudio({
  src: '/audio/tracks/blank.mp3',
  attribution: '"Blank" by Disfigure [NCS Release] - https://ncs.io/Blank',
  data: onsets,
});

function buildChart(variant: Variant): PulseChart {
  const builder = new ChartBuilder({
    id: buildVariantSlug('blank', variant),
    slug: buildVariantSlug('blank', variant),
    title: buildVariantTitle('Blank', variant),
    description:
      'Mid-tempo melodic dubstep at 150 BPM. Snare-driven backbeat with bright synth leads - upper-medium with rapid lead clusters in the drop.',
    bpm: onsets.bpm,
    difficulty: 3,
    variant,
    spawnSurface: 'floor',
    palette: PALETTE_GLACIER,
    audio,
  });

  // 60s @ 150 BPM ≈ 150 beats. Build → main drop → break → second drop.
  builder.section({ startBeat: 0, endBeat: 16, kind: 'verse' });
  builder.section({ startBeat: 16, endBeat: 64, kind: 'drop' });
  builder.section({ startBeat: 64, endBeat: 80, kind: 'bridge' });
  builder.section({ startBeat: 80, endBeat: 150, kind: 'drop' });

  // Build: snare backbeat + early lead phrases. Leads are detector-bounded
  // at intensity 0.25, so 0.18+ catches just the prominent peaks while
  // snares carry the main pulse.
  const buildPads = variantSelectBeats(variant, onsets, {
    fromBeat: 4,
    untilBeat: 16,
    kinds: ['snare', 'lead'],
    minIntensity: 0.18,
    minSpacing: 1,
  });

  // Main drop: snares as rhythm spine, strongest leads as melodic accents.
  // Snare 0.35+ catches the backbeat without picking up every off-beat
  // shimmer; cross-kind merge at 150 BPM uses 0.5-beat floor (= 200ms) to
  // keep a snare-on-N + lead-on-N.25 from producing 100ms double-taps.
  const drop1 = variantMergeBeatsWithSpacing(
    variant,
    0.5,
    variantSelectBeats(variant, onsets, {
      fromBeat: 16,
      untilBeat: 64,
      kinds: ['snare'],
      minIntensity: 0.35,
      minSpacing: 0.5,
    }),
    variantSelectBeats(variant, onsets, {
      fromBeat: 16,
      untilBeat: 64,
      kinds: ['lead'],
      minIntensity: 0.2,
      minSpacing: 1,
    }),
  );

  // Break: lead-only - the song clears out the drums for a melodic interlude.
  const bridgePads = variantSelectBeats(variant, onsets, {
    fromBeat: 64,
    untilBeat: 80,
    kinds: ['lead'],
    minIntensity: 0.18,
    minSpacing: 1.5,
  });

  // Second drop: matches first drop density. `untilBeat: 148` stops two
  // beats early so the very end of the cut is empty space (the audio
  // fades out there).
  const drop2 = variantMergeBeatsWithSpacing(
    variant,
    0.5,
    variantSelectBeats(variant, onsets, {
      fromBeat: 80,
      untilBeat: 148,
      kinds: ['snare'],
      minIntensity: 0.35,
      minSpacing: 0.5,
    }),
    variantSelectBeats(variant, onsets, {
      fromBeat: 80,
      untilBeat: 148,
      kinds: ['lead'],
      minIntensity: 0.2,
      minSpacing: 1,
    }),
  );

  const padBeats = mergeBeats(buildPads, drop1, bridgePads, drop2);
  for (const beat of padBeats) {
    builder.pad(beat);
  }

  // The pre-drop riser and mid-track break both leave long quiet stretches.
  for (const c of findBurstChambers(padBeats, {
    fromBeat: 4,
    untilBeat: 148,
    minGapBeats: 6,
  })) {
    builder.burstChamber(c);
  }

  builder.setTotalBeats(150);
  return builder.build();
}

export const BLANK_VARIANTS: Record<Variant, PulseChart> = Object.fromEntries(
  VARIANTS.map((v) => [v, buildChart(v)]),
) as Record<Variant, PulseChart>;

export const BLANK_EXPECTED_REPLAYS: Record<Variant, ReplayInputs> =
  Object.fromEntries(
    VARIANTS.map((v) => [v, expectedReplayFromPads(BLANK_VARIANTS[v])]),
  ) as Record<Variant, ReplayInputs>;
